// Tariff Usage Report — Bulk tab and the XLSX exports of both tabs. Pure: the
// service loads the sites and the gap rows (RPC bv_interval_gaps), this groups,
// filters, sorts, summarises and builds the export rows.
//
// Month and day rules come from usageGaps so By NMI and Bulk agree: a month's
// expected days stop at `lastDay` (yesterday), a month wholly after it is
// dropped, and a day is covered when any interval row exists for it.

import type { ExportSheet } from '../utils/exportUtils';
import { addMonths, monthLabel } from './billMonths';
import type { ExpectedSite } from './missingBills';
import { matchesSiteFilters, siteValue, type SiteFilterKey, type SiteFilterValues } from './siteFilters';
import { expectedInMonth } from './siteStatus';
import { usageGaps, type MonthGap } from './usageGaps';
import type { BvBill } from '../types/billTypes';

export const MAX_BULK_MONTHS = 24;

/** The filters the Bulk tab offers (no tariff: the plan lists these four). */
export const BULK_FILTER_KEYS: SiteFilterKey[] = ['entity', 'retailer', 'distributor', 'state'];

/** One row of RPC bv_interval_gaps. */
export type IntervalGapRow = { site_id: string; month: string; missing_dates: string[] };

export type SiteCoverage = {
  site: ExpectedSite;
  /** Months the site was expected in and that have a gap, newest first. */
  months: MonthGap[];
  monthsShort: number;
  daysMissing: number;
};

export type MonthSummary = { month: string; sitesChecked: number; sitesWithGaps: number; daysMissing: number };

export type BulkCoverage = {
  rows: SiteCoverage[];
  sitesChecked: number;
  fullyCovered: number;
  withGaps: number;
  byMonth: MonthSummary[];
};

// Fixed, not Intl: en-AU abbreviates September as "Sept".
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Dates as day ranges per month: `['2026-09-03','2026-09-04','2026-09-05','2026-09-18']`
 * → `3–5, 18 Sep`. Several months are joined with `; `. Input order does not matter.
 */
export function gapRanges(dates: string[]): string {
  const byMonth = new Map<string, number[]>();
  for (const d of [...new Set(dates)].sort()) {
    const key = d.slice(0, 7);
    if (!byMonth.has(key)) byMonth.set(key, []);
    byMonth.get(key)!.push(Number(d.slice(8, 10)));
  }
  return [...byMonth].map(([month, days]) => {
    const runs: string[] = [];
    for (let i = 0; i < days.length;) {
      let j = i;
      while (j + 1 < days.length && days[j + 1] === days[j] + 1) j++;
      runs.push(i === j ? String(days[i]) : `${days[i]}–${days[j]}`);
      i = j + 1;
    }
    return `${runs.join(', ')} ${MONTH_SHORT[Number(month.slice(5, 7)) - 1]}`;
  }).join('; ');
}

/** From/to months put in order and limited to `max` months (the `to` end is kept). */
export function clampMonthRange(from: string, to: string, max = MAX_BULK_MONTHS): [string, string] {
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  const earliest = addMonths(hi, -(max - 1));
  return [lo < earliest ? earliest : lo, hi];
}

/** A site as the shared site filters read it (they read a bill's joined site). */
const asBill = (site: ExpectedSite) => ({ site }) as unknown as Pick<BvBill, 'site'>;

export function coverageSiteValue(site: ExpectedSite, key: SiteFilterKey): string {
  return siteValue(asBill(site), key);
}

/** The values each Bulk dropdown offers, from every site loaded. */
export function bulkFilterOptions(sites: ExpectedSite[]): Record<SiteFilterKey, string[]> {
  const out = {} as Record<SiteFilterKey, string[]>;
  for (const key of BULK_FILTER_KEYS) {
    out[key] = [...new Set(sites.map((s) => coverageSiteValue(s, key)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }
  return out;
}

/**
 * The Bulk report. A site is checked when it matches the filters and was
 * expected (enrolled) in at least one month of the range; only its expected
 * months count. Rows are sites with any gap, worst first: most days missing,
 * then most months short, then NMI.
 */
/**
 * The sites the Bulk tab reports on: matching the filters and expected in at
 * least one month of the period. The page asks the RPC for exactly these, since
 * buildBulkCoverage reads a site with no gap rows as fully covered.
 */
export function bulkSitesToCheck(
  sites: ExpectedSite[], fromMonth: string, toMonth: string, lastDay: string, filters: SiteFilterValues,
): ExpectedSite[] {
  const months = usageGaps(new Set(), fromMonth, toMonth, lastDay).map((m) => m.month);
  return sites.filter((s) => matchesSiteFilters(asBill(s), filters) && months.some((m) => expectedInMonth(s.bv_extn, m)));
}

/**
 * Site ids in RPC batches of about `siteMonths` site-months each (167): every
 * call stays well inside the 8 s statement timeout and returns fewer than
 * PostgREST's 1,000 rows (at most one row per site-month).
 */
export function gapBatches(ids: string[], months: number, siteMonths = 250): string[][] {
  const size = Math.max(1, Math.floor(siteMonths / Math.max(1, months)));
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += size) batches.push(ids.slice(i, i + size));
  return batches;
}

export function buildBulkCoverage(
  sites: ExpectedSite[], gaps: IntervalGapRow[], fromMonth: string, toMonth: string,
  lastDay: string, filters: SiteFilterValues,
): BulkCoverage {
  // Every month's expected days, by the By NMI rule.
  const monthDays = usageGaps(new Set(), fromMonth, toMonth, lastDay);
  const missingBySite = new Map<string, Set<string>>();
  for (const g of gaps) {
    if (!missingBySite.has(g.site_id)) missingBySite.set(g.site_id, new Set());
    const set = missingBySite.get(g.site_id)!;
    for (const d of g.missing_dates) set.add(d);
  }

  const byMonth = new Map<string, MonthSummary>(
    monthDays.map((m) => [m.month, { month: m.month, sitesChecked: 0, sitesWithGaps: 0, daysMissing: 0 }]),
  );
  const rows: SiteCoverage[] = [];
  let sitesChecked = 0;

  for (const site of sites) {
    if (!matchesSiteFilters(asBill(site), filters)) continue;
    const expected = monthDays.filter((m) => expectedInMonth(site.bv_extn, m.month));
    if (expected.length === 0) continue;
    sitesChecked++;
    const missing = missingBySite.get(site.id) ?? new Set<string>();
    const months: MonthGap[] = [];
    for (const m of expected) {
      const summary = byMonth.get(m.month)!;
      summary.sitesChecked++;
      const missingDays = m.missingDays.filter((d) => missing.has(d));
      if (missingDays.length === 0) continue;
      summary.sitesWithGaps++;
      summary.daysMissing += missingDays.length;
      months.push({ month: m.month, daysExpected: m.daysExpected, missingDays });
    }
    if (months.length === 0) continue;
    rows.push({
      site,
      months: months.reverse(),
      monthsShort: months.length,
      daysMissing: months.reduce((n, m) => n + m.missingDays.length, 0),
    });
  }

  rows.sort((a, b) => b.daysMissing - a.daysMissing
    || b.monthsShort - a.monthsShort
    || (a.site.nmi ?? '').localeCompare(b.site.nmi ?? ''));

  return {
    rows,
    sitesChecked,
    fullyCovered: sitesChecked - rows.length,
    withGaps: rows.length,
    byMonth: [...byMonth.values()].reverse(),
  };
}

/** Bulk XLSX: one row per site-month with a gap, then the Summary sheet. */
export function bulkCoverageSheets(report: BulkCoverage, fromMonth: string, toMonth: string): ExportSheet[] {
  const gapRows = report.rows.flatMap(({ site, months }) => months.map((m) => ({
    NMI: site.nmi ?? '',
    Entity: coverageSiteValue(site, 'entity'),
    Retailer: coverageSiteValue(site, 'retailer'),
    Month: monthLabel(m.month),
    'Days expected': m.daysExpected,
    'Days missing': m.missingDays.length,
    'Gap dates': gapRanges(m.missingDays),
  })));
  const summary: Record<string, unknown>[] = [
    { Measure: 'Period', Value: `${monthLabel(fromMonth)} – ${monthLabel(toMonth)}` },
    { Measure: 'Sites checked', Value: report.sitesChecked },
    { Measure: 'Fully covered', Value: report.fullyCovered },
    { Measure: 'With gaps', Value: report.withGaps },
    ...report.byMonth.map((m) => ({
      Measure: monthLabel(m.month),
      Value: `${m.sitesWithGaps} of ${m.sitesChecked} sites with gaps, ${m.daysMissing} days missing`,
    })),
  ];
  return [{ name: 'Gaps by site-month', rows: gapRows }, { name: 'Summary', rows: summary }];
}

/** By NMI XLSX: the months table, then one row per missing day. */
export function byNmiCoverageSheets(nmi: string, months: MonthGap[]): ExportSheet[] {
  const monthRows = months.map((m) => ({
    NMI: nmi,
    Month: monthLabel(m.month),
    'Days with data': m.daysExpected - m.missingDays.length,
    'Days expected': m.daysExpected,
    'Days missing': m.missingDays.length,
    'Gap dates': gapRanges(m.missingDays),
  }));
  const dayRows = months.flatMap((m) => m.missingDays.map((d) => ({ NMI: nmi, Month: monthLabel(m.month), Date: d })));
  return [{ name: 'Months', rows: monthRows }, { name: 'Missing days', rows: dayRows }];
}
